import { useState } from "react";
import AuthGate, { useAuth } from "./AuthGate";

const SCHOOL_PHOTO =
  "https://resources.finalsite.net/images/f_auto,q_auto,t_image_size_2/v1739980569/srivernjorg/tfsmdbn3ttaurne9ukrc/Copyof20240829_DIG_14_1.jpg";

const BUILDINGS = [
  "ELC",
  "High School",
  "Elementary School",
  "Primary School",
  "Middle School",
  "Annex",
];

const initialForm = {
  firstName: "",
  lastName: "",
  personalEmail: "",
  startDate: "",
  staffLaptopNeeded: false,
  schoolEmailNeeded: false,
  idBadgeNeeded: false,
  buildingAccess: "",
  buildingOther: "",
  genesisNeeded: false,
};

const encode = (data) => new URLSearchParams(data).toString();

export default function App() {
  return (
    <AuthGate>
      <OnboardingTracker />
    </AuthGate>
  );
}

function OnboardingTracker() {
  const { user } = useAuth() || {};

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [showPopup, setShowPopup] = useState(false);

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const e = {};

    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";

    if (!form.personalEmail.trim()) {
      e.personalEmail = "Personal email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.personalEmail)) {
      e.personalEmail = "Please enter a valid email address";
    }

    if (!form.startDate) e.startDate = "Start date is required";

    if (form.idBadgeNeeded && !form.buildingAccess) {
      e.buildingAccess = "Please select a building";
    }

    if (
      form.idBadgeNeeded &&
      form.buildingAccess === "Other" &&
      !form.buildingOther.trim()
    ) {
      e.buildingOther = "Please specify the building name";
    }

    return e;
  };

  const handleSubmit = async () => {
  const e = validate();

  if (Object.keys(e).length) {
    setErrors(e);
    return;
  }

  setStatus("loading");

  try {
    const buildingFinal =
      form.buildingAccess === "Other"
        ? form.buildingOther.trim()
        : form.buildingAccess;

    const payload = {
      access_key: "d522788c-887c-49ea-9e9c-13e1c516d3de",

      subject: `New Staff Onboarding - ${form.firstName} ${form.lastName}`,

      from_name: "SRPS Onboarding Portal",

      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      personalEmail: form.personalEmail.trim(),
      startDate: form.startDate,

      staffLaptopNeeded: form.staffLaptopNeeded ? "Yes" : "No",
      schoolEmailNeeded: form.schoolEmailNeeded ? "Yes" : "No",
      idBadgeNeeded: form.idBadgeNeeded ? "Yes" : "No",

      buildingAccess: form.idBadgeNeeded
        ? buildingFinal
        : "N/A",

      genesisNeeded: form.genesisNeeded ? "Yes" : "No",

      submittedBy: user?.email || "",
    };

    const response = await fetch(
      "https://api.web3forms.com/submit",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const result = await response.json();

    if (result.success) {
      setStatus("success");
      setShowPopup(true);
    } else {
      setStatus("error");
    }
  } catch {
    setStatus("error");
  }
};

  const handleClose = () => {
    setShowPopup(false);
    setForm(initialForm);
    setErrors({});
    setStatus("idle");
  };

  const inp = (key) => ({
    style: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: 8,
      boxSizing: "border-box",
      border: `1.5px solid ${errors[key] ? "#dc2626" : "#dde3ea"}`,
      fontSize: 14,
      outline: "none",
      background: "#fafbfc",
      fontFamily: "inherit",
      color: "#1a1a1a",
      transition: "all 0.15s",
    },
    onFocus: (e) => {
      e.target.style.borderColor = "#8B1A2E";
      e.target.style.background = "#fff";
      e.target.style.boxShadow = "0 0 0 3px rgba(139,26,46,0.1)";
    },
    onBlur: (e) => {
      e.target.style.borderColor = errors[key] ? "#dc2626" : "#dde3ea";
      e.target.style.background = "#fafbfc";
      e.target.style.boxShadow = "none";
    },
  });

  return (
    <>
      <form
        name="onboarding"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        hidden
      >
        <input type="hidden" name="form-name" value="onboarding" />
        <input type="text" name="firstName" />
        <input type="text" name="lastName" />
        <input type="email" name="personalEmail" />
        <input type="text" name="startDate" />
        <input type="text" name="staffLaptopNeeded" />
        <input type="text" name="schoolEmailNeeded" />
        <input type="text" name="idBadgeNeeded" />
        <input type="text" name="buildingAccess" />
        <input type="text" name="genesisNeeded" />
        <input type="text" name="submittedBy" />
      </form>

      <div
        style={{
          minHeight: "100vh",
          background: "#f2f0ee",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
        }}
      >
        <div style={{ position: "relative", height: 220, overflow: "hidden" }}>
          <img
            src={SCHOOL_PHOTO}
            alt="South River Public Schools"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 30%",
              display: "block",
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, rgba(100,10,25,0.55) 0%, rgba(80,8,20,0.82) 100%)",
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "rgba(255,220,210,0.85)",
                letterSpacing: 3,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              South River Public Schools
            </div>

            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: "#fff",
                lineHeight: 1.2,
                textShadow: "0 2px 12px rgba(0,0,0,0.4)",
                marginBottom: 10,
              }}
            >
              Welcome to SR Onboarding
            </div>

            <div
              style={{
                fontSize: 13,
                color: "rgba(255,235,230,0.88)",
                maxWidth: 380,
                lineHeight: 1.5,
                fontWeight: 400,
              }}
            >
              Please complete this form to request IT setup for new staff. Our
              team will be notified automatically.
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 40,
              background: "linear-gradient(to bottom, transparent, #f2f0ee)",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 20px 60px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 540 }}>
            {status === "error" && (
              <div
                style={{
                  background: "#fff5f5",
                  border: "1px solid #fca5a5",
                  borderRadius: 10,
                  padding: "14px 18px",
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div>
                  <div
                    style={{ fontWeight: 700, color: "#dc2626", fontSize: 13 }}
                  >
                    Submission failed
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Could not reach the server. Please try again or contact IT.
                  </div>
                </div>
                <button
                  onClick={() => setStatus("idle")}
                  style={{
                    marginLeft: "auto",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 18,
                    color: "#9ca3af",
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 6px 32px rgba(0,0,0,0.09)",
                border: "1px solid #e6e2df",
                overflow: "hidden",
              }}
            >
              {status === "loading" && (
                <div
                  style={{
                    height: 3,
                    background: "#f0e8e8",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: "40%",
                      background: "linear-gradient(90deg,#8B1A2E,#c94060)",
                      animation: "sh 1.2s infinite ease-in-out",
                    }}
                  />
                  <style>{`@keyframes sh{0%{margin-left:-40%}100%{margin-left:100%}}`}</style>
                </div>
              )}

              <div style={{ padding: "26px 28px 0" }}>
                <SectionHeader>👤 Personal Information</SectionHeader>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                    marginBottom: 18,
                  }}
                >
                  <div>
                    <FL>First Name *</FL>
                    <input
                      value={form.firstName}
                      placeholder="Sarah"
                      onChange={(e) => set("firstName", e.target.value)}
                      {...inp("firstName")}
                    />
                    {errors.firstName && <Err>{errors.firstName}</Err>}
                  </div>

                  <div>
                    <FL>Last Name *</FL>
                    <input
                      value={form.lastName}
                      placeholder="Johnson"
                      onChange={(e) => set("lastName", e.target.value)}
                      {...inp("lastName")}
                    />
                    {errors.lastName && <Err>{errors.lastName}</Err>}
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <FL>Personal Email Address *</FL>
                  <input
                    value={form.personalEmail}
                    type="email"
                    placeholder="sarah.johnson@gmail.com"
                    onChange={(e) => set("personalEmail", e.target.value)}
                    {...inp("personalEmail")}
                  />
                  <Hint>
                    We'll use this to send login info before their school email
                    is ready.
                  </Hint>
                  {errors.personalEmail && <Err>{errors.personalEmail}</Err>}
                </div>

                <div style={{ marginBottom: 8 }}>
                  <FL>Start Date *</FL>
                  <input
                    value={form.startDate}
                    type="date"
                    onChange={(e) => set("startDate", e.target.value)}
                    {...inp("startDate")}
                  />
                  {errors.startDate && <Err>{errors.startDate}</Err>}
                </div>
              </div>

              <Divider />

              <div style={{ padding: "0 28px 8px" }}>
                <SectionHeader>🔧 Access & Equipment Needed</SectionHeader>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <Toggle
                    on={form.staffLaptopNeeded}
                    onToggle={() =>
                      set("staffLaptopNeeded", !form.staffLaptopNeeded)
                    }
                    icon="💻"
                    label="Staff Laptop Needed"
                    desc="Issue a school laptop on start date"
                    disabled={status === "loading"}
                  />

                  <Toggle
                    on={form.schoolEmailNeeded}
                    onToggle={() =>
                      set("schoolEmailNeeded", !form.schoolEmailNeeded)
                    }
                    icon="📧"
                    label="School Email Account"
                    desc="Create a @srschools.org email address"
                    disabled={status === "loading"}
                  />

                  <Toggle
                    on={form.idBadgeNeeded}
                    onToggle={() => {
                      set("idBadgeNeeded", !form.idBadgeNeeded);
                      set("buildingAccess", "");
                      set("buildingOther", "");
                    }}
                    icon="🪪"
                    label="ID Badge & Building Access"
                    desc="Issue a photo ID badge with door access"
                    disabled={status === "loading"}
                  />

                  {form.idBadgeNeeded && (
                    <div
                      style={{
                        marginLeft: 0,
                        padding: "16px 18px",
                        background: "#fdf5f6",
                        border: "1px solid #f0c8cf",
                        borderRadius: 10,
                      }}
                    >
                      <FL>Select Building *</FL>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginBottom: errors.buildingAccess ? 6 : 0,
                        }}
                      >
                        {BUILDINGS.map((b) => {
                          const sel = form.buildingAccess === b;

                          return (
                            <button
                              key={b}
                              type="button"
                              onClick={() => {
                                set("buildingAccess", b);
                                set("buildingOther", "");
                              }}
                              style={{
                                padding: "7px 16px",
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: "pointer",
                                transition: "all 0.15s",
                                border: "2px solid",
                                borderColor: sel ? "#8B1A2E" : "#ddd",
                                background: sel ? "#8B1A2E" : "#fff",
                                color: sel ? "#fff" : "#374151",
                              }}
                            >
                              {b}
                            </button>
                          );
                        })}

                        <button
                          type="button"
                          onClick={() => set("buildingAccess", "Other")}
                          style={{
                            padding: "7px 16px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            border: "2px solid",
                            borderColor:
                              form.buildingAccess === "Other"
                                ? "#8B1A2E"
                                : "#ddd",
                            background:
                              form.buildingAccess === "Other"
                                ? "#8B1A2E"
                                : "#fff",
                            color:
                              form.buildingAccess === "Other"
                                ? "#fff"
                                : "#374151",
                          }}
                        >
                          Other
                        </button>
                      </div>

                      {errors.buildingAccess && (
                        <Err>{errors.buildingAccess}</Err>
                      )}

                      {form.buildingAccess === "Other" && (
                        <div style={{ marginTop: 12 }}>
                          <FL>Please specify *</FL>
                          <input
                            value={form.buildingOther}
                            placeholder="e.g. District Office, Bus Depot..."
                            onChange={(e) =>
                              set("buildingOther", e.target.value)
                            }
                            {...inp("buildingOther")}
                          />
                          {errors.buildingOther && (
                            <Err>{errors.buildingOther}</Err>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <Toggle
                    on={form.genesisNeeded}
                    onToggle={() => set("genesisNeeded", !form.genesisNeeded)}
                    icon="🔐"
                    label="Genesis SIS Access"
                    desc="Student Information System — view & manage student records, grades, attendance, and scheduling"
                    disabled={status === "loading"}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: "22px 28px 26px",
                  background: "#faf8f7",
                  borderTop: "1px solid #ede8e5",
                  marginTop: 20,
                }}
              >
                <button
                  onClick={handleSubmit}
                  disabled={status === "loading"}
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: 10,
                    border: "none",
                    background:
                      status === "loading"
                        ? "#c9a0a8"
                        : "linear-gradient(135deg, #8B1A2E 0%, #6b1222 100%)",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 15,
                    cursor: status === "loading" ? "default" : "pointer",
                    boxShadow:
                      status === "loading"
                        ? "none"
                        : "0 4px 16px rgba(139,26,46,0.35)",
                    transition: "all 0.2s",
                    letterSpacing: 0.2,
                  }}
                >
                  {status === "loading"
                    ? "⏳  Submitting..."
                    : "🚀  Submit Onboarding Request"}
                </button>

                <div
                  style={{
                    textAlign: "center",
                    marginTop: 10,
                    fontSize: 11,
                    color: "#9ca3af",
                  }}
                >
                  🔒 Saved to Netlify Forms · IT team notified by email
                </div>
              </div>
            </div>
          </div>
        </div>

        {showPopup && (
          <>
            <div
              onClick={handleClose}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
                zIndex: 100,
                backdropFilter: "blur(3px)",
                animation: "fadein 0.2s ease",
              }}
            />

            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 101,
                width: "90%",
                maxWidth: 420,
                background: "#fff",
                borderRadius: 20,
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                overflow: "hidden",
                animation: "popin 0.25s cubic-bezier(0.34,1.56,0.64,1)",
              }}
            >
              <style>{`@keyframes fadein{from{opacity:0}to{opacity:1}} @keyframes popin{from{opacity:0;transform:translate(-50%,-46%) scale(0.92)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

              <div
                style={{
                  background: "linear-gradient(135deg, #8B1A2E, #6b1222)",
                  padding: "32px 28px 28px",
                  textAlign: "center",
                  color: "#fff",
                }}
              >
                <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 900,
                    marginBottom: 8,
                  }}
                >
                  Thank You for Your Submission!
                </div>
                <div style={{ fontSize: 13, opacity: 0.88, lineHeight: 1.5 }}>
                  IT will be notified and will begin setting up access for{" "}
                  <strong>
                    {form.firstName} {form.lastName}
                  </strong>
                  .
                </div>
              </div>

              <div style={{ padding: "24px 28px 28px" }}>
                <div
                  style={{
                    background: "#fdf5f6",
                    border: "1px solid #f0c8cf",
                    borderRadius: 10,
                    padding: "16px 18px",
                    marginBottom: 22,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#8B1A2E",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 10,
                    }}
                  >
                    What happens next
                  </div>

                  {[
                    "✉️  IT admins notified by email",
                    "📋  Entry saved in Netlify Forms",
                    "🔧  Provisioning begins before start date",
                  ].map((t) => (
                    <div
                      key={t}
                      style={{
                        fontSize: 13,
                        color: "#374151",
                        marginBottom: 7,
                      }}
                    >
                      {t}
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleClose}
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 9,
                    border: "none",
                    background: "linear-gradient(135deg, #8B1A2E, #6b1222)",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 14,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(139,26,46,0.3)",
                  }}
                >
                  Submit Another Staff Member
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function SectionHeader({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#8B1A2E",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 18,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{ height: 1, background: "#f0ece9", margin: "20px 0 20px" }}
    />
  );
}

function FL({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 800,
        color: "#374151",
        marginBottom: 7,
        textTransform: "uppercase",
        letterSpacing: 0.6,
      }}
    >
      {children}
    </label>
  );
}

function Hint({ children }) {
  return (
    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
      {children}
    </div>
  );
}

function Err({ children }) {
  return (
    <div style={{ color: "#dc2626", fontSize: 11, marginTop: 5 }}>
      ⚠ {children}
    </div>
  );
}

function Toggle({ on, onToggle, icon, label, desc, disabled }) {
  return (
    <div
      onClick={!disabled ? onToggle : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "13px 16px",
        borderRadius: 10,
        cursor: disabled ? "default" : "pointer",
        userSelect: "none",
        transition: "all 0.15s",
        border: `1.5px solid ${on ? "#8B1A2E" : "#e2e8f0"}`,
        background: on ? "#fdf5f6" : "#fafbfc",
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          flexShrink: 0,
          transition: "all 0.15s",
          border: `2px solid ${on ? "#8B1A2E" : "#c9d4e0"}`,
          background: on ? "#8B1A2E" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {on && (
          <span style={{ color: "#fff", fontSize: 13, lineHeight: 1 }}>✓</span>
        )}
      </div>

      <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>

      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a" }}>
          {label}
        </div>
        {desc && (
          <div
            style={{
              fontSize: 11,
              color: "#6b7280",
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            {desc}
          </div>
        )}
      </div>

      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          padding: "3px 10px",
          borderRadius: 5,
          flexShrink: 0,
          background: on ? "#fde8eb" : "#f1f5f9",
          color: on ? "#8B1A2E" : "#94a3b8",
        }}
      >
        {on ? "YES" : "NO"}
      </span>
    </div>
  );
}